import React, { useState } from "react";
import {
	Badge,
	Box,
	Button,
	IconButton,
	Popover,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
	useNotifications,
	NotificationItem,
} from "../../hooks/useNotifications";

const severityColor: Record<NotificationItem["severity"], string> = {
	info: "#2BA6AC",
	success: "#2e7d32",
	warning: "#ed6c02",
	error: "#d32f2f",
};

function severityIcon(severity: NotificationItem["severity"]) {
	const sx = { fontSize: 18, color: severityColor[severity] };
	switch (severity) {
		case "success":
			return <CheckCircleOutlineIcon sx={sx} />;
		case "warning":
			return <WarningAmberIcon sx={sx} />;
		case "error":
			return <ErrorOutlineIcon sx={sx} />;
		default:
			return <InfoOutlinedIcon sx={sx} />;
	}
}

function relativeTime(iso: string): string {
	const then = new Date(iso).getTime();
	const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
	if (diffSec < 60) return "just now";
	if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
	if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
	if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
	return new Date(iso).toLocaleDateString();
}

const NotificationBell: React.FC = () => {
	const { items, unreadCount, markRead, markAllRead, dismiss } =
		useNotifications();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const open = Boolean(anchorEl);

	const handleClickItem = (n: NotificationItem) => {
		if (n.read_at == null) markRead(n.id);
		if (n.href) {
			window.location.href = n.href;
		}
	};

	return (
		<>
			<Tooltip title="Notifications">
				<IconButton
					aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
					onClick={(e) => setAnchorEl(e.currentTarget)}
					sx={{ mr: 0.5 }}
				>
					<Badge
						badgeContent={unreadCount}
						color="error"
						overlap="circular"
						sx={{
							"& .MuiBadge-badge": {
								fontSize: 10,
								height: 16,
								minWidth: 16,
								padding: "0 4px",
							},
						}}
					>
						{unreadCount > 0 ? (
							<NotificationsActiveIcon
								sx={{ color: "#2BA6AC" }}
							/>
						) : (
							<NotificationsNoneIcon sx={{ color: "#5A6E7F" }} />
						)}
					</Badge>
				</IconButton>
			</Tooltip>

			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				PaperProps={{
					sx: {
						width: 380,
						maxWidth: "calc(100vw - 32px)",
						maxHeight: 480,
						borderRadius: "12px",
						boxShadow: "0 10px 30px rgba(26,43,74,0.18)",
						overflow: "hidden",
					},
				}}
			>
				{/* Header */}
				<Box
					sx={{
						px: 2,
						py: 1.5,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						borderBottom: "1px solid rgba(0,0,0,0.06)",
					}}
				>
					<Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
						Notifications
					</Typography>
					{unreadCount > 0 && (
						<Button
							size="small"
							onClick={markAllRead}
							sx={{
								fontSize: "12px",
								textTransform: "none",
								color: "#2BA6AC",
							}}
						>
							Mark all read
						</Button>
					)}
				</Box>

				{/* List */}
				<Box sx={{ maxHeight: 380, overflowY: "auto" }}>
					{items.length === 0 ? (
						<Box
							sx={{
								px: 3,
								py: 5,
								textAlign: "center",
								color: "#8094A8",
							}}
						>
							<NotificationsNoneIcon
								sx={{ fontSize: 36, opacity: 0.4 }}
							/>
							<Typography sx={{ fontSize: "13px", mt: 1 }}>
								You're all caught up.
							</Typography>
						</Box>
					) : (
						<Stack
							divider={
								<Box
									sx={{
										borderBottom:
											"1px solid rgba(0,0,0,0.04)",
									}}
								/>
							}
						>
							{items.map((n) => {
								const isUnread = n.read_at == null;
								return (
									<Box
										key={n.id}
										onClick={() => handleClickItem(n)}
										sx={{
											position: "relative",
											display: "flex",
											gap: 1.5,
											px: 2,
											py: 1.5,
											cursor: n.href
												? "pointer"
												: "default",
											background: isUnread
												? "rgba(43,166,172,0.04)"
												: "transparent",
											transition: "background 0.15s ease",
											"&:hover": {
												background: isUnread
													? "rgba(43,166,172,0.08)"
													: "rgba(0,0,0,0.02)",
											},
											"&:hover .notif-dismiss": {
												opacity: 1,
											},
										}}
									>
										<Box sx={{ pt: 0.25 }}>
											{severityIcon(n.severity)}
										</Box>
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<Typography
												sx={{
													fontSize: "13px",
													fontWeight: isUnread
														? 700
														: 500,
													color: "#1A2B4A",
													lineHeight: 1.3,
												}}
											>
												{n.title}
											</Typography>
											<Typography
												sx={{
													fontSize: "12px",
													color: "#5A6E7F",
													mt: 0.25,
													lineHeight: 1.4,
													display: "-webkit-box",
													WebkitLineClamp: 2,
													WebkitBoxOrient: "vertical",
													overflow: "hidden",
												}}
											>
												{n.body}
											</Typography>
											<Typography
												sx={{
													fontSize: "11px",
													color: "#8094A8",
													mt: 0.5,
												}}
											>
												{relativeTime(n.created_at)}
											</Typography>
										</Box>
										<IconButton
											className="notif-dismiss"
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												dismiss(n.id);
											}}
											sx={{
												alignSelf: "flex-start",
												opacity: 0,
												transition:
													"opacity 0.15s ease",
												color: "#8094A8",
											}}
											aria-label="Dismiss notification"
										>
											<CloseIcon sx={{ fontSize: 16 }} />
										</IconButton>
									</Box>
								);
							})}
						</Stack>
					)}
				</Box>
			</Popover>
		</>
	);
};

export default NotificationBell;
